import { useEffect, useMemo, useRef, useState } from 'react';
import { Dispatch, bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { FormattedMessage, injectIntl, useIntl } from 'react-intl';
import { toast } from 'react-toastify';

import { RootState } from '../../reducers';
import * as actions from '../../actions/generalActions';
import * as adminActions from '../../actions/admin';
import MikaConfirmModal from '../../components/MikaConfirmModal/MikaConfirmModal';
import { translatedText } from '../../components/TranslatedText';
import KubitSyncProgressCard from './KubitSyncProgressCard';
import KubitSyncRunHistory from './KubitSyncRunHistory';

import './kubitIntegration.scss';

// Frontend polling cadence for the sync stepper. Backend writes per-step
// progress with a 500ms throttle, so polling at 800ms gives the UI 1-2
// fresh frames per second without hammering the server. Lowering this
// without lowering the backend throttle wastes round-trips.
const SYNC_PROGRESS_POLL_MS = 800;
const TERMINAL_RUN_STATUSES = ['success', 'partial', 'error', 'aborted'];

interface KubitSettings {
    id: number;
    companyId: number;
    apiKeyLastFour: string;
    apiBaseUrl: string;
    enabled: boolean;
    pushOnSave: boolean;
    autoConsumeEnabled: boolean;
    pollingIntervalMinutes: number;
    defaults: {
        countryId: number | null;
        entityTypeId: number | null;
        phonePrefixId: number | null;
    };
    cursors: {
        branches: string | null;
        entities: string | null;
        items: string | null;
        itemPrices: string | null;
    };
    lastFullSyncAt: string | null;
    lastSyncAt: string | null;
    lastSyncStatus: string | null;
}

interface Props {
    history: any;
    buttonLoader: boolean;
    admin: any;
    actions: any;
    adminActions: any;
}

const DEFAULT_BASE_URL = 'https://demoapi.kubitlite.com';

const KubitIntegration = (props: Props) => {
    const intl = useIntl();

    const settings: KubitSettings | null = props?.admin?.kubitSettings || null;
    const isConnected = !!settings && settings.enabled;
    const isConfigured = !!settings;

    const [apiKey, setApiKey] = useState('');
    const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE_URL);
    const [testing, setTesting] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
    const [showResetCursorConfirm, setShowResetCursorConfirm] = useState(false);
    const [showPurgeMappingsConfirm, setShowPurgeMappingsConfirm] = useState(false);

    const syncResult = props?.admin?.kubitSyncResult || null;
    const logRows: any[] = props?.admin?.kubitSyncLog?.data || [];
    const discovery = props?.admin?.kubitDiscovery || { countryIds: [], entityTypeIds: [], phonePrefixIds: [] };

    const [defaultCountryId, setDefaultCountryId] = useState<number | null>(null);
    const [defaultEntityTypeId, setDefaultEntityTypeId] = useState<number | null>(null);
    const [defaultPhonePrefixId, setDefaultPhonePrefixId] = useState<number | null>(null);
    const [savingDefaults, setSavingDefaults] = useState(false);

    const [pushOnSave, setPushOnSave] = useState<boolean>(true);
    const [autoConsumeEnabled, setAutoConsumeEnabled] = useState<boolean>(false);
    const [pollingIntervalMinutes, setPollingIntervalMinutes] = useState<number>(10);
    const [savingPreferences, setSavingPreferences] = useState(false);

    useEffect(() => {
        props?.adminActions?.getKubitSettings();
    }, []);

    useEffect(() => {
        if (isConnected) {
            props?.adminActions?.getKubitSyncLog({ perPage: 20 });
            props?.adminActions?.getKubitDiscovery();
        }
    }, [isConnected]);

    useEffect(() => {
        if (settings?.defaults) {
            setDefaultCountryId(settings.defaults.countryId);
            setDefaultEntityTypeId(settings.defaults.entityTypeId);
            setDefaultPhonePrefixId(settings.defaults.phonePrefixId);
        }
    }, [settings?.defaults?.countryId, settings?.defaults?.entityTypeId, settings?.defaults?.phonePrefixId]);

    useEffect(() => {
        if (settings) {
            setPushOnSave(!!settings.pushOnSave);
            setAutoConsumeEnabled(!!settings.autoConsumeEnabled);
            setPollingIntervalMinutes(settings.pollingIntervalMinutes || 10);
        }
    }, [settings?.pushOnSave, settings?.autoConsumeEnabled, settings?.pollingIntervalMinutes]);

    // ─── Sync stepper live-progress polling ────────────────────────────────
    // Read state from Redux. The card receives these as props.
    const syncProgress = props?.admin?.kubitSyncProgress || { run: null, steps: [] };
    const syncProgressRun = syncProgress.run;
    const syncProgressSteps = syncProgress.steps || [];
    const isRunInFlight = syncProgressRun !== null && syncProgressRun.status === 'running';

    // ─── Per-button running-ops tracking (the 6 single-stage buttons) ──────
    // The sync button uses isRunInFlight from polling instead, since sync's
    // duration is governed by the backend run row, not a frontend promise.
    type SingleStageOpKey =
        | 'pushEntities'
        | 'pushItems'
        | 'pushSuppliers'
        | 'reconcileEntities'
        | 'reconcileItems'
        | 'reconcileSuppliers';

    const [runningOps, setRunningOps] = useState<{ [K in SingleStageOpKey]?: boolean }>({});
    const setOp = (key: SingleStageOpKey, value: boolean) =>
        setRunningOps(prev => ({ ...prev, [key]: value }));
    const isOpRunning = (key: SingleStageOpKey) => !!runningOps[key];

    // Top progress bar appears whenever ANY of the 7 ops is in flight: the
    // sync (driven by polling state) OR any of the 6 single-stage buttons.
    // Gives the admin one ambient "something's happening" cue regardless of
    // which button they clicked or where on the page they're scrolled.
    const anyOpInFlight = isRunInFlight || Object.values(runningOps).some(Boolean);

    // Helper: show a pending toast while a promise is in flight; auto-dismiss
    // when it settles. The existing success/error toasts in each handler's
    // .then chain continue to fire as before — this only adds the
    // immediate-feedback "we got your click" message that disappears when the
    // operation finishes. Without this, a 30s push felt like a frozen page.
    function withLoadingToast<T>(
        messageId: string,
        defaultMessage: string,
        promise: Promise<T>
    ): Promise<T> {
        // Older react-toastify (v8) doesn't expose toast.loading; use info()
        // with autoClose=false so the toast stays pinned until we manually
        // dismiss it on promise settlement. The .finally below covers both
        // resolve and reject paths.
        const loadingId = toast.info(
            intl.formatMessage({ id: messageId, defaultMessage }),
            { autoClose: false, closeOnClick: false, draggable: false }
        );
        promise.finally(() => toast.dismiss(loadingId));
        return promise;
    }

    // Polling interval handle stored in a ref so cleanup works correctly
    // across renders. We also stash the last known run_uuid so we can
    // detect a status transition out of `running` reliably even if the
    // interval fires after the state has already updated.
    const pollIntervalRef = useRef<number | null>(null);

    function startPolling() {
        if (pollIntervalRef.current !== null) {
            return; // already polling
        }
        // Fire one immediately so the stepper appears within a frame of
        // the click rather than waiting up to 800ms.
        props?.adminActions?.getKubitSyncProgress();
        pollIntervalRef.current = window.setInterval(() => {
            props?.adminActions?.getKubitSyncProgress();
        }, SYNC_PROGRESS_POLL_MS);
    }

    function stopPolling() {
        if (pollIntervalRef.current !== null) {
            window.clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
        }
    }

    // On mount, do a single fetch so we pick up any active run from a
    // prior browser session (refresh during sync). If it's still running,
    // resume the polling cycle automatically.
    useEffect(() => {
        if (!isConnected) return;
        props?.adminActions?.getKubitSyncProgress();
    }, [isConnected]);

    // React to status transitions: start polling when running, stop when
    // terminal. This is the source of truth for the polling lifecycle —
    // handleSyncNow just kicks off the sync; this effect handles the rest.
    useEffect(() => {
        if (isRunInFlight) {
            startPolling();
        } else {
            stopPolling();
        }
        return () => stopPolling();
    }, [isRunInFlight]);

    // Belt-and-suspenders cleanup if the component unmounts while polling.
    useEffect(() => {
        return () => stopPolling();
    }, []);

    function renderDefaultDropdown(
        labelId: string,
        labelDefault: string,
        options: Array<{ id: number; count: number }>,
        value: number | null,
        setter: (v: number | null) => void
    ) {
        return (
            <div className="kubit-field">
                <label>
                    <FormattedMessage id={labelId} defaultMessage={labelDefault} />
                </label>
                <select
                    value={value ?? ''}
                    onChange={(e) => setter(e.target.value === '' ? null : Number(e.target.value))}
                    disabled={options.length === 0}
                >
                    <option value="">
                        {intl.formatMessage({ id: 'integrations.kubit.defaults.unset', defaultMessage: '— not set —' })}
                    </option>
                    {options.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                            {intl.formatMessage(
                                { id: 'integrations.kubit.defaults.optionLabel', defaultMessage: 'ID {id} ({count} entities)' },
                                { id: opt.id, count: opt.count }
                            )}
                        </option>
                    ))}
                </select>
                {options.length === 0 && (
                    <small>
                        <FormattedMessage
                            id="integrations.kubit.defaults.noData"
                            defaultMessage="No data discovered yet — run a sync first."
                        />
                    </small>
                )}
            </div>
        );
    }

    /**
     * Health pill computed locally from lastSyncStatus + age relative to
     * the configured polling interval. We give the tenant 2.5x grace before
     * stale-warning so a single missed cron tick doesn't flip the pill.
     */
    const healthPill = useMemo(() => {
        if (!isConfigured || !isConnected) return null;
        if (!settings?.lastSyncAt) {
            return { tone: 'idle' as const, key: 'integrations.kubit.health.idle', defaultMessage: 'Awaiting first sync' };
        }
        const ageMs = Date.now() - new Date(settings.lastSyncAt).getTime();
        const intervalMs = (settings.pollingIntervalMinutes || 10) * 60 * 1000;
        const stale = ageMs > intervalMs * 2.5;
        const status = settings.lastSyncStatus;
        if (status === 'error') {
            return { tone: 'error' as const, key: 'integrations.kubit.health.error', defaultMessage: 'Last sync failed' };
        }
        if (status === 'partial') {
            return { tone: 'warn' as const, key: 'integrations.kubit.health.partial', defaultMessage: 'Last sync partial' };
        }
        if (stale) {
            return { tone: 'warn' as const, key: 'integrations.kubit.health.stale', defaultMessage: 'Last sync is stale' };
        }
        return { tone: 'success' as const, key: 'integrations.kubit.health.healthy', defaultMessage: 'Healthy' };
    }, [isConfigured, isConnected, settings?.lastSyncAt, settings?.lastSyncStatus, settings?.pollingIntervalMinutes]);

    function handleSavePreferences() {
        setSavingPreferences(true);
        props?.adminActions
            ?.saveKubitSettings({
                pushOnSave: pushOnSave,
                autoConsumeEnabled: autoConsumeEnabled,
                pollingIntervalMinutes: pollingIntervalMinutes,
            }, false)
            .then((ok: boolean) => {
                setSavingPreferences(false);
                if (ok) {
                    toast.info(translatedText({
                        id: 'integrations.kubit.toast.preferencesSaved',
                        defaultMessage: 'Preferences saved.'
                    }));
                }
            })
            .catch(() => setSavingPreferences(false));
    }

    function handleSaveDefaults() {
        setSavingDefaults(true);
        props?.adminActions
            ?.saveKubitSettings({
                defaultCountryId: defaultCountryId,
                defaultEntityTypeId: defaultEntityTypeId,
                defaultPhonePrefixId: defaultPhonePrefixId,
            }, false)
            .then((ok: boolean) => {
                setSavingDefaults(false);
                if (ok) {
                    toast.info(translatedText({
                        id: 'integrations.kubit.toast.defaultsSaved',
                        defaultMessage: 'Defaults saved.'
                    }));
                }
            })
            .catch(() => setSavingDefaults(false));
    }

    function handleSyncNow() {
        // Fire the POST but DO NOT await its 1–2 minute duration. The
        // request runs to completion server-side; we use the run_uuid in
        // the response only for log/settings refresh after it returns.
        // The user-visible UX is driven by the parallel polling cycle that
        // startPolling kicks off below, which surfaces live per-step state
        // via the KubitSyncProgressCard.
        setSyncing(true);
        startPolling();

        props?.adminActions
            ?.triggerKubitSync()
            .then((result: any) => {
                setSyncing(false);
                if (!result) return;

                if (result.locked) {
                    // Lock-rejected immediately — backend never started.
                    // Polling will see the aborted run and clear itself,
                    // but show a toast so the user knows it didn't run.
                    toast.warning(intl.formatMessage(
                        { id: 'integrations.kubit.toast.syncLocked', defaultMessage: 'Sync already in progress.' }
                    ));
                    return;
                }

                // The stepper card already showed live progress; no need
                // for the bulky per-resource toast we used to fire here.
                // Only surface a terminal failure toast if the rolled-up
                // result indicates one — most users will see the stepper
                // card's red header instead, but the toast remains as a
                // fallback for users who collapsed/dismissed it.
                const failed: string[] = [];
                Object.entries(result.results || {}).forEach(([resource, r]: any) => {
                    if (resource === '_unhandled') return;
                    if (resource.startsWith('reconcile_')) return;
                    if (!(r?.ok)) failed.push(resource);
                });

                if (failed.length > 0) {
                    toast.error(intl.formatMessage(
                        { id: 'integrations.kubit.toast.syncPartial', defaultMessage: 'Sync finished with errors on: {resources}' },
                        { resources: failed.join(', ') }
                    ));
                }

                // Refresh related data so the rest of the page reflects
                // the new state (cursors, log, etc).
                props?.adminActions?.getKubitSettings();
                props?.adminActions?.getKubitSyncLog({ perPage: 20 });

                // One more progress fetch immediately on resolution to
                // ensure the card shows the FINAL terminal state even if
                // the polling tick hasn't fired yet.
                props?.adminActions?.getKubitSyncProgress();

                // Explicitly stop polling here, NOT via the status-watcher
                // useEffect. Why: if the sync ran fast enough that no
                // polling tick observed isRunInFlight=true (timing race
                // between the POST and the first poll's view of the new
                // run row), the watcher never sees a true→false transition
                // and its cleanup never fires — interval started by
                // startPolling() above keeps running forever. Stopping
                // here unconditionally on POST resolution avoids that.
                // 150ms delay so the getKubitSyncProgress() above lands
                // and the card reflects the final state before polling
                // halts.
                setTimeout(() => stopPolling(), 150);
            })
            .catch(() => {
                // Same explicit-stop reasoning as the .then path. If the
                // backend is still running while the frontend POST timed
                // out, the polling already-in-flight responses had ample
                // chance to set isRunInFlight=true and the watcher's
                // running-step branch will keep polling alive on its own.
                // Stopping here only halts polls that have nothing to
                // observe (no run, or already-terminal run) — the watcher
                // would re-engage if a fresh sync is detected on the next
                // page interaction.
                setSyncing(false);
                stopPolling();
            });
    }

    useEffect(() => {
        if (settings?.apiBaseUrl) {
            setBaseUrl(settings.apiBaseUrl);
        }
    }, [settings?.apiBaseUrl]);

    const maskedKey = useMemo(() => {
        if (!settings?.apiKeyLastFour) return null;
        return intl.formatMessage(
            { id: 'integrations.kubit.apiKey.masked', defaultMessage: '{prefix}••••{lastFour}' },
            { prefix: 'kbt_', lastFour: settings.apiKeyLastFour }
        );
    }, [settings?.apiKeyLastFour, intl]);

    function handleTest() {
        if (!baseUrl || !apiKey) {
            toast.error(translatedText({
                id: 'integrations.kubit.errors.required',
                defaultMessage: 'This field is required.'
            }));
            return;
        }
        setTesting(true);
        props?.adminActions
            ?.testKubitConnection({ apiKey, apiBaseUrl: baseUrl })
            .then((result: any) => {
                setTesting(false);
                if (result?.ok) {
                    toast.info(translatedText({
                        id: 'integrations.kubit.toast.testSuccess',
                        defaultMessage: 'Connection test succeeded.'
                    }));
                } else {
                    toast.error(intl.formatMessage(
                        { id: 'integrations.kubit.toast.testFailed', defaultMessage: 'Connection test failed: {message}' },
                        { message: result?.message || '—' }
                    ));
                }
            })
            .catch(() => {
                setTesting(false);
                toast.error(translatedText({
                    id: 'integrations.kubit.toast.error',
                    defaultMessage: 'Something went wrong.'
                }));
            });
    }

    function handleSave() {
        if (!isConfigured && !apiKey) {
            toast.error(translatedText({
                id: 'integrations.kubit.errors.required',
                defaultMessage: 'This field is required.'
            }));
            return;
        }
        const payload: any = { apiBaseUrl: baseUrl };
        if (apiKey) payload.apiKey = apiKey;

        props?.adminActions
            ?.saveKubitSettings(payload, !isConfigured)
            .then((ok: boolean) => {
                if (ok) {
                    setApiKey('');
                    toast.info(translatedText({
                        id: 'integrations.kubit.toast.saved',
                        defaultMessage: 'KuBIT integration saved.'
                    }));
                }
            });
    }

    function handleDisconnect() {
        setShowDisconnectConfirm(false);
        props?.adminActions
            ?.disconnectKubit()
            .then((ok: boolean) => {
                if (ok) {
                    toast.info(translatedText({
                        id: 'integrations.kubit.toast.disconnected',
                        defaultMessage: 'KuBIT integration disconnected.'
                    }));
                }
            });
    }

    function handlePushSuppliers() {
        setOp('pushSuppliers', true);
        withLoadingToast(
            'integrations.kubit.toast.pushingSuppliers',
            'Sending suppliers to KuBIT…',
            props?.adminActions?.pushKubitSuppliers()
        )
            .then((result: any) => {
                if (!result) return;
                const values = {
                    pushed: result.pushed ?? 0,
                    skipped: result.skipped ?? 0,
                    created: result.created ?? 0,
                    errors: result.errors ?? 0,
                };
                if (values.errors > 0) {
                    toast.error(intl.formatMessage(
                        {
                            id: 'integrations.kubit.toast.pushedSuppliersPartial',
                            defaultMessage: 'Updated {pushed}, created {created}, skipped {skipped}, errors {errors}.'
                        },
                        values
                    ));
                } else {
                    toast.success(intl.formatMessage(
                        {
                            id: 'integrations.kubit.toast.pushedSuppliers',
                            defaultMessage: 'Updated {pushed}, created {created}, skipped {skipped}.'
                        },
                        values
                    ));
                }
                props?.adminActions?.getKubitSettings();
                props?.adminActions?.getKubitSyncLog({ perPage: 20 });
            })
            .finally(() => setOp('pushSuppliers', false));
    }

    function handlePushItems() {
        setOp('pushItems', true);
        withLoadingToast(
            'integrations.kubit.toast.pushingItems',
            'Sending articles to KuBIT…',
            props?.adminActions?.pushKubitItems()
        )
            .then((result: any) => {
                if (!result) return;
                const values = {
                    pushed: result.pushed ?? 0,
                    skipped: result.skipped ?? 0,
                    created: result.created ?? 0,
                    errors: result.errors ?? 0,
                };
                if (values.errors > 0) {
                    toast.error(intl.formatMessage(
                        {
                            id: 'integrations.kubit.toast.pushedItemsPartial',
                            defaultMessage: 'Updated {pushed}, created {created}, skipped {skipped}, errors {errors}.'
                        },
                        values
                    ));
                } else {
                    toast.success(intl.formatMessage(
                        {
                            id: 'integrations.kubit.toast.pushedItems',
                            defaultMessage: 'Updated {pushed}, created {created}, skipped {skipped}.'
                        },
                        values
                    ));
                }
                props?.adminActions?.getKubitSettings();
                props?.adminActions?.getKubitSyncLog({ perPage: 20 });
            })
            .finally(() => setOp('pushItems', false));
    }

    function handlePushEntities() {
        setOp('pushEntities', true);
        withLoadingToast(
            'integrations.kubit.toast.pushingEntities',
            'Sending clients to KuBIT…',
            props?.adminActions?.pushKubitEntities()
        )
            .then((result: any) => {
                if (!result) return;
                const values = {
                    pushed: result.pushed ?? 0,
                    skipped: result.skipped ?? 0,
                    created: result.created ?? 0,
                    errors: result.errors ?? 0,
                };
                if (values.errors > 0) {
                    toast.error(intl.formatMessage(
                        {
                            id: 'integrations.kubit.toast.pushedEntitiesPartial',
                            defaultMessage: 'Updated {pushed}, created {created}, skipped {skipped}, errors {errors}.'
                        },
                        values
                    ));
                } else {
                    toast.success(intl.formatMessage(
                        {
                            id: 'integrations.kubit.toast.pushedEntities',
                            defaultMessage: 'Updated {pushed}, created {created}, skipped {skipped}.'
                        },
                        values
                    ));
                }
                props?.adminActions?.getKubitSettings();
                props?.adminActions?.getKubitSyncLog({ perPage: 20 });
            })
            .finally(() => setOp('pushEntities', false));
    }

    function handleReconcileEntities() {
        setOp('reconcileEntities', true);
        withLoadingToast(
            'integrations.kubit.toast.reconcilingEntities',
            'Linking entities to Mika clients…',
            props?.adminActions?.reconcileKubitEntities()
        )
            .then((result: any) => {
                if (!result) return;
                toast.success(intl.formatMessage(
                    {
                        id: 'integrations.kubit.toast.reconciled',
                        defaultMessage: 'Linked {linked}, created {created}, skipped {skipped}, fields updated {fieldsUpdated}, potential duplicates {potentialDuplicate} (suppliers skipped: {suppliers}).'
                    },
                    {
                        linked: result.linked ?? 0,
                        created: result.created ?? 0,
                        skipped: result.skipped ?? 0,
                        suppliers: result.suppliers_skipped ?? 0,
                        fieldsUpdated: result.fields_updated ?? 0,
                        potentialDuplicate: result.potential_duplicate ?? 0,
                    }
                ));
                props?.adminActions?.getKubitSyncLog({ perPage: 20 });
            })
            .finally(() => setOp('reconcileEntities', false));
    }

    function handleReconcileSuppliers() {
        setOp('reconcileSuppliers', true);
        withLoadingToast(
            'integrations.kubit.toast.reconcilingSuppliers',
            'Linking suppliers to Mika suppliers…',
            props?.adminActions?.reconcileKubitSuppliers()
        )
            .then((result: any) => {
                if (!result) return;
                toast.success(intl.formatMessage(
                    {
                        id: 'integrations.kubit.toast.reconciledSuppliers',
                        defaultMessage: 'Linked {linked}, created {created}, skipped {skipped}.'
                    },
                    {
                        linked: result.linked ?? 0,
                        created: result.created ?? 0,
                        skipped: result.skipped ?? 0,
                    }
                ));
                props?.adminActions?.getKubitSyncLog({ perPage: 20 });
            })
            .finally(() => setOp('reconcileSuppliers', false));
    }

    function handleReconcileItems() {
        setOp('reconcileItems', true);
        withLoadingToast(
            'integrations.kubit.toast.reconcilingItems',
            'Linking items to Mika articles…',
            props?.adminActions?.reconcileKubitItems()
        )
            .then((result: any) => {
                if (!result) return;
                toast.success(intl.formatMessage(
                    {
                        id: 'integrations.kubit.toast.reconciledItems',
                        defaultMessage: 'Linked {linked}, created {created}, skipped {skipped}, prices updated {pricesUpdated}, suppliers attached {suppliersAttached}.'
                    },
                    {
                        linked: result.linked ?? 0,
                        created: result.created ?? 0,
                        skipped: result.skipped ?? 0,
                        pricesUpdated: result.prices_updated ?? 0,
                        suppliersAttached: result.suppliers_attached ?? 0,
                    }
                ));
                props?.adminActions?.getKubitSyncLog({ perPage: 20 });
            })
            .finally(() => setOp('reconcileItems', false));
    }

    function handleResetCursor() {
        setShowResetCursorConfirm(false);
        props?.adminActions
            ?.resetKubitCursor()
            .then((ok: boolean) => {
                if (ok) {
                    toast.info(translatedText({
                        id: 'integrations.kubit.toast.cursorReset',
                        defaultMessage: 'Cursors reset. Next sync will pull everything from KuBIT.'
                    }));
                    props?.adminActions?.getKubitSyncLog({ perPage: 20 });
                }
            });
    }

    function handlePurgeMappings() {
        setShowPurgeMappingsConfirm(false);
        props?.adminActions
            ?.purgeKubitMappings()
            .then((deleted: number | null) => {
                if (deleted !== null) {
                    toast.info(intl.formatMessage(
                        { id: 'integrations.kubit.toast.mappingsPurged', defaultMessage: '{count} mapping(s) purged.' },
                        { count: deleted }
                    ));
                }
            });
    }

    return (
        <div className="admin-page kubit-integration-page">
            {/* Top-of-page progress bar — ambient "something's happening"
                cue that's visible no matter where on the page the user is
                scrolled. Shows whenever ANY of the 7 KuBIT operations is
                in flight: sync (driven by polling state) OR any of the 6
                single-stage buttons (push x3, reconcile x3). Kept aria-hidden
                because the per-button spinners and toasts already announce
                state to screen readers. */}
            {anyOpInFlight && (
                <div className="kubit-progress-bar" aria-hidden="true" />
            )}
            <div className="kubit-header">
                <h2 className="pageTitle">
                    <FormattedMessage id="integrations.kubit.title" defaultMessage="KuBIT Integration" />
                </h2>
                <p className="kubit-subtitle">
                    <FormattedMessage
                        id="integrations.kubit.subtitle"
                        defaultMessage="Connect Mika to your KuBIT account to sync items and entities."
                    />
                </p>
            </div>

            <div className="kubit-card">
                <h3 className="kubit-section-title">
                    <FormattedMessage id="integrations.kubit.connection.title" defaultMessage="Connection" />
                </h3>

                <div className="kubit-field">
                    <label htmlFor="kubit-api-key">
                        <FormattedMessage id="integrations.kubit.apiKey.label" defaultMessage="API Key" />
                    </label>
                    <input
                        id="kubit-api-key"
                        type="password"
                        autoComplete="off"
                        value={apiKey}
                        placeholder={intl.formatMessage({
                            id: 'integrations.kubit.apiKey.placeholder',
                            defaultMessage: 'kbt_xxxx_xxxxxxxxxxxxxxxx'
                        })}
                        onChange={(e) => setApiKey(e.target.value)}
                    />
                    <small>
                        {isConfigured ? (
                            <FormattedMessage
                                id="integrations.kubit.apiKey.keepUnchanged"
                                defaultMessage="Leave blank to keep the existing key."
                            />
                        ) : (
                            <FormattedMessage
                                id="integrations.kubit.apiKey.help"
                                defaultMessage="Generate one in KuBIT under Konfigurimet → API Keys."
                            />
                        )}
                    </small>
                </div>

                <div className="kubit-field">
                    <label htmlFor="kubit-base-url">
                        <FormattedMessage id="integrations.kubit.baseUrl.label" defaultMessage="KuBIT Base URL" />
                    </label>
                    <input
                        id="kubit-base-url"
                        type="url"
                        value={baseUrl}
                        placeholder={intl.formatMessage({
                            id: 'integrations.kubit.baseUrl.placeholder',
                            defaultMessage: 'https://demoapi.kubitlite.com'
                        })}
                        onChange={(e) => setBaseUrl(e.target.value)}
                    />
                </div>

                <div className="kubit-actions kubit-actions-spread">
                    <button
                        type="button"
                        className="kubit-btn"
                        disabled={testing || props?.buttonLoader || !apiKey || !baseUrl}
                        onClick={handleTest}
                    >
                        <FormattedMessage id="integrations.kubit.test" defaultMessage="Test Connection" />
                    </button>
                    <button
                        type="button"
                        className="kubit-btn is-primary"
                        disabled={props?.buttonLoader}
                        onClick={handleSave}
                    >
                        {isConfigured
                            ? <FormattedMessage id="integrations.kubit.save" defaultMessage="Save" />
                            : <FormattedMessage id="integrations.kubit.connect" defaultMessage="Connect" />
                        }
                    </button>
                </div>
            </div>

            <div className="kubit-card">
                <h3 className="kubit-section-title">
                    <FormattedMessage id="integrations.kubit.status.title" defaultMessage="Status" />
                </h3>

                <div className="kubit-status-grid">
                    <div className="kubit-status-row">
                        <span className="kubit-status-label">
                            <FormattedMessage id="integrations.kubit.status.title" defaultMessage="Status" />
                        </span>
                        <span className={`kubit-status-pill ${isConnected ? 'is-connected' : 'is-disconnected'}`}>
                            {!isConfigured && (
                                <FormattedMessage
                                    id="integrations.kubit.status.notConfigured"
                                    defaultMessage="Not configured"
                                />
                            )}
                            {isConfigured && isConnected && (
                                <FormattedMessage
                                    id="integrations.kubit.status.connected"
                                    defaultMessage="Connected"
                                />
                            )}
                            {isConfigured && !isConnected && (
                                <FormattedMessage
                                    id="integrations.kubit.status.disconnected"
                                    defaultMessage="Disconnected"
                                />
                            )}
                        </span>
                    </div>

                    {healthPill && (
                        <div className="kubit-status-row">
                            <span className="kubit-status-label">
                                <FormattedMessage id="integrations.kubit.health.label" defaultMessage="Health" />
                            </span>
                            <span className={`kubit-health-pill is-${healthPill.tone}`}>
                                <FormattedMessage id={healthPill.key} defaultMessage={healthPill.defaultMessage} />
                            </span>
                        </div>
                    )}

                    {isConfigured && (
                        <>
                            <div className="kubit-status-row">
                                <span className="kubit-status-label">
                                    <FormattedMessage id="integrations.kubit.status.apiKey" defaultMessage="API key" />
                                </span>
                                <span className="kubit-status-value mono">{maskedKey || '—'}</span>
                            </div>
                            <div className="kubit-status-row">
                                <span className="kubit-status-label">
                                    <FormattedMessage id="integrations.kubit.status.baseUrl" defaultMessage="Base URL" />
                                </span>
                                <span className="kubit-status-value mono">{settings?.apiBaseUrl}</span>
                            </div>
                            <div className="kubit-status-row">
                                <span className="kubit-status-label">
                                    <FormattedMessage id="integrations.kubit.status.lastSync" defaultMessage="Last sync" />
                                </span>
                                <span className="kubit-status-value">
                                    {settings?.lastSyncAt
                                        ? new Date(settings.lastSyncAt).toLocaleString()
                                        : <FormattedMessage id="integrations.kubit.status.never" defaultMessage="never" />}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/*
                    Hidden 2026-05-04 per user request. The hex `rowVersion`
                    cursors are diagnostic-only data that means nothing to
                    end-user admins. Devs / KuBIT support can still read
                    them directly from `kubit_integrations.last_*_row_version`
                    via Adminer / tinker when debugging sync issues. The
                    "Reset cursors" danger-zone button still works (it
                    operates on the same DB columns).

                    To restore: uncomment the block below.

                    {isConfigured && (
                        <div className="kubit-cursors">
                            {(['branches', 'entities', 'items', 'itemPrices'] as const).map((res) => (
                                <div className="kubit-cursor-row" key={res}>
                                    <span className="kubit-cursor-label">{res}</span>
                                    <span className="kubit-cursor-value mono">
                                        {settings?.cursors?.[res] || '—'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}
                */}

                {/* Old `.kubit-timings` widget removed — the new
                    KubitSyncProgressCard (rendered below the Sync button)
                    is a strict superset: it shows all 8 step durations
                    plus live state during sync plus per-step counters. */}

                {isConnected && (
                    <div className="kubit-actions-spread">
                        <div className="kubit-action-group">
                            <div>
                                <p className="kubit-action-group-title">
                                    <FormattedMessage id="integrations.kubit.actions.sync" defaultMessage="Sync" />
                                </p>
                                <p className="kubit-action-group-desc">
                                    <FormattedMessage id="integrations.kubit.actions.sync.desc" defaultMessage="Pull the latest data from KuBIT into Mika." />
                                </p>
                            </div>
                            <div className="kubit-actions">
                                <button
                                    type="button"
                                    className="kubit-btn is-primary"
                                    disabled={syncing || isRunInFlight || props?.buttonLoader}
                                    onClick={handleSyncNow}
                                >
                                    <FormattedMessage
                                        id={(syncing || isRunInFlight) ? 'integrations.kubit.syncing' : 'integrations.kubit.syncNow'}
                                        defaultMessage={(syncing || isRunInFlight) ? 'Syncing…' : 'Sync now'}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Live-progress stepper card. Renders when there's
                            any run (active OR most-recent finished). The
                            component handles its own auto-collapse 30s
                            after completion and the show-details re-expand. */}
                        {syncProgressRun !== null && (
                            <KubitSyncProgressCard
                                run={syncProgressRun}
                                steps={syncProgressSteps}
                                allowAutoCollapse={true}
                                autoCollapseAfterMs={30_000}
                            />
                        )}

                        <div className="kubit-action-group">
                            <div>
                                <p className="kubit-action-group-title">
                                    <FormattedMessage id="integrations.kubit.actions.push" defaultMessage="Push to KuBIT" />
                                </p>
                                <p className="kubit-action-group-desc">
                                    <FormattedMessage id="integrations.kubit.actions.push.desc" defaultMessage="Send Mika's clients, articles and suppliers to KuBIT." />
                                </p>
                            </div>
                            <div className="kubit-actions">
                                <button
                                    type="button"
                                    className="kubit-btn"
                                    disabled={isOpRunning('pushEntities') || props?.buttonLoader}
                                    onClick={handlePushEntities}
                                >
                                    <FormattedMessage
                                        id={isOpRunning('pushEntities') ? 'integrations.kubit.pushingEntities' : 'integrations.kubit.pushEntities'}
                                        defaultMessage={isOpRunning('pushEntities') ? 'Sending…' : 'Push clients to KuBIT'}
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="kubit-btn"
                                    disabled={isOpRunning('pushItems') || props?.buttonLoader}
                                    onClick={handlePushItems}
                                >
                                    <FormattedMessage
                                        id={isOpRunning('pushItems') ? 'integrations.kubit.pushingItems' : 'integrations.kubit.pushItems'}
                                        defaultMessage={isOpRunning('pushItems') ? 'Sending…' : 'Push articles to KuBIT'}
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="kubit-btn"
                                    disabled={isOpRunning('pushSuppliers') || props?.buttonLoader}
                                    onClick={handlePushSuppliers}
                                >
                                    <FormattedMessage
                                        id={isOpRunning('pushSuppliers') ? 'integrations.kubit.pushingSuppliers' : 'integrations.kubit.pushSuppliers'}
                                        defaultMessage={isOpRunning('pushSuppliers') ? 'Sending…' : 'Push suppliers to KuBIT'}
                                    />
                                </button>
                            </div>
                        </div>

                        <div className="kubit-action-group">
                            <div>
                                <p className="kubit-action-group-title">
                                    <FormattedMessage id="integrations.kubit.actions.reconcile" defaultMessage="Reconcile" />
                                </p>
                                <p className="kubit-action-group-desc">
                                    <FormattedMessage id="integrations.kubit.actions.reconcile.desc" defaultMessage="Match KuBIT entries with existing Mika data so they share the same record." />
                                </p>
                            </div>
                            <div className="kubit-actions">
                                <button
                                    type="button"
                                    className="kubit-btn"
                                    disabled={isOpRunning('reconcileEntities') || props?.buttonLoader}
                                    onClick={handleReconcileEntities}
                                >
                                    <FormattedMessage
                                        id={isOpRunning('reconcileEntities') ? 'integrations.kubit.reconcilingEntities' : 'integrations.kubit.reconcileEntities'}
                                        defaultMessage={isOpRunning('reconcileEntities') ? 'Linking…' : 'Link entities to Mika clients'}
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="kubit-btn"
                                    disabled={isOpRunning('reconcileSuppliers') || props?.buttonLoader}
                                    onClick={handleReconcileSuppliers}
                                >
                                    <FormattedMessage
                                        id={isOpRunning('reconcileSuppliers') ? 'integrations.kubit.reconcilingSuppliers' : 'integrations.kubit.reconcileSuppliers'}
                                        defaultMessage={isOpRunning('reconcileSuppliers') ? 'Linking…' : 'Link suppliers to Mika suppliers'}
                                    />
                                </button>
                                <button
                                    type="button"
                                    className="kubit-btn"
                                    disabled={isOpRunning('reconcileItems') || props?.buttonLoader}
                                    onClick={handleReconcileItems}
                                >
                                    <FormattedMessage
                                        id={isOpRunning('reconcileItems') ? 'integrations.kubit.reconcilingItems' : 'integrations.kubit.reconcileItems'}
                                        defaultMessage={isOpRunning('reconcileItems') ? 'Linking…' : 'Link items to Mika articles'}
                                    />
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {isConnected && (
                <div className="kubit-card">
                    <h3 className="kubit-section-title">
                        <FormattedMessage id="integrations.kubit.preferences.title" defaultMessage="Preferences" />
                    </h3>

                    <div className="kubit-field kubit-toggle-row">
                        <label htmlFor="kubit-push-on-save" className="kubit-toggle-label">
                            <input
                                id="kubit-push-on-save"
                                type="checkbox"
                                checked={pushOnSave}
                                onChange={(e) => setPushOnSave(e.target.checked)}
                            />
                            <span className="kubit-toggle-text">
                                <FormattedMessage
                                    id="integrations.kubit.preferences.pushOnSave"
                                    defaultMessage="Push client edits to KuBIT in real time"
                                />
                            </span>
                        </label>
                        <small>
                            <FormattedMessage
                                id="integrations.kubit.preferences.pushOnSaveHelp"
                                defaultMessage="When on, every Mika client save is mirrored to KuBIT immediately. Turn off to defer pushes to manual ‘Push clients to KuBIT’ runs only."
                            />
                        </small>
                    </div>

                    <div className="kubit-field kubit-toggle-row">
                        <label htmlFor="kubit-auto-consume" className="kubit-toggle-label">
                            <input
                                id="kubit-auto-consume"
                                type="checkbox"
                                checked={autoConsumeEnabled}
                                onChange={(e) => setAutoConsumeEnabled(e.target.checked)}
                            />
                            <span className="kubit-toggle-text">
                                <FormattedMessage
                                    id="integrations.kubit.preferences.autoConsume"
                                    defaultMessage="Consume raw materials automatically when finished"
                                />
                            </span>
                        </label>
                        <small>
                            <FormattedMessage
                                id="integrations.kubit.preferences.autoConsumeHelp"
                                defaultMessage="When on, finishing a material's production status consumes its KuBIT raw-material stock automatically. Turn off to consume manually with the ‘Consume in KuBIT’ button on each finished material."
                            />
                        </small>
                    </div>

                    <div className="kubit-field">
                        <label htmlFor="kubit-polling-interval">
                            <FormattedMessage
                                id="integrations.kubit.preferences.pollingInterval"
                                defaultMessage="Sync frequency"
                            />
                        </label>
                        <select
                            id="kubit-polling-interval"
                            value={pollingIntervalMinutes}
                            onChange={(e) => setPollingIntervalMinutes(Number(e.target.value))}
                        >
                            {[5, 10, 15, 30, 60].map((mins) => (
                                <option key={mins} value={mins}>
                                    {intl.formatMessage(
                                        { id: 'integrations.kubit.preferences.pollingIntervalOption', defaultMessage: 'Every {mins} minutes' },
                                        { mins }
                                    )}
                                </option>
                            ))}
                        </select>
                        <small>
                            <FormattedMessage
                                id="integrations.kubit.preferences.pollingIntervalHelp"
                                defaultMessage="How often the scheduled sync runs against KuBIT. Lower means fresher data but more API calls."
                            />
                        </small>
                    </div>

                    <div className="kubit-actions kubit-actions-spread">
                        <button
                            type="button"
                            className="kubit-btn is-primary"
                            disabled={savingPreferences || props?.buttonLoader}
                            onClick={handleSavePreferences}
                        >
                            <FormattedMessage id="integrations.kubit.preferences.save" defaultMessage="Save preferences" />
                        </button>
                    </div>
                </div>
            )}

            {isConnected && (
                <div className="kubit-card">
                    <h3 className="kubit-section-title">
                        <FormattedMessage
                            id="integrations.kubit.history.title"
                            defaultMessage="Sync history"
                        />
                    </h3>
                    <KubitSyncRunHistory
                        runs={props?.admin?.kubitSyncRuns || { data: [], meta: null }}
                        runDetailCache={props?.admin?.kubitSyncRunDetailCache || {}}
                        onLoadPage={(page, perPage) => props?.adminActions?.getKubitSyncRuns({ page, perPage })}
                        onLoadDetail={(runUuid) => props?.adminActions?.getKubitSyncRunDetail(runUuid)}
                        perPage={10}
                    />
                </div>
            )}

            {isConnected && (
                <div className="kubit-card">
                    <h3 className="kubit-section-title">
                        <FormattedMessage id="integrations.kubit.activity.title" defaultMessage="Activity" />
                    </h3>
                    {logRows.length === 0 ? (
                        <p className="kubit-disabled-note">
                            <FormattedMessage
                                id="integrations.kubit.activity.empty"
                                defaultMessage="No sync activity recorded yet. Click ‘Sync now’ to pull data from KuBIT."
                            />
                        </p>
                    ) : (
                        <table className="kubit-log-table">
                            <thead>
                                <tr>
                                    <th><FormattedMessage id="integrations.kubit.activity.time" defaultMessage="Time" /></th>
                                    <th><FormattedMessage id="integrations.kubit.activity.resource" defaultMessage="Resource" /></th>
                                    <th><FormattedMessage id="integrations.kubit.activity.direction" defaultMessage="Direction" /></th>
                                    <th><FormattedMessage id="integrations.kubit.activity.operation" defaultMessage="Operation" /></th>
                                    <th><FormattedMessage id="integrations.kubit.activity.status" defaultMessage="Status" /></th>
                                    <th><FormattedMessage id="integrations.kubit.activity.message" defaultMessage="Message" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {logRows.map((row: any) => (
                                    <tr key={row.id}>
                                        <td>{row.attemptedAt ? new Date(row.attemptedAt).toLocaleString() : '—'}</td>
                                        <td>{row.resourceType}</td>
                                        <td>{row.direction}</td>
                                        <td>{row.operation}</td>
                                        <td>
                                            <span className={`kubit-status-pill ${row.status === 'ok' ? 'is-connected' : 'is-error'}`}>
                                                {row.status}
                                            </span>
                                        </td>
                                        <td className="kubit-log-message">{row.message || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {isConnected && (
                <div className="kubit-card">
                    <h3 className="kubit-section-title">
                        <FormattedMessage id="integrations.kubit.defaults.title" defaultMessage="Defaults" />
                    </h3>
                    <p className="kubit-disabled-note">
                        <FormattedMessage
                            id="integrations.kubit.defaults.help"
                            defaultMessage="These IDs are used when Mika creates new entities/items in KuBIT. Auto-discovered from synced entities — most-frequent values appear first."
                        />
                    </p>

                    {renderDefaultDropdown(
                        'integrations.kubit.defaults.country',
                        'Default country',
                        discovery.countryIds,
                        defaultCountryId,
                        setDefaultCountryId
                    )}
                    {renderDefaultDropdown(
                        'integrations.kubit.defaults.entityType',
                        'Default entity type',
                        discovery.entityTypeIds,
                        defaultEntityTypeId,
                        setDefaultEntityTypeId
                    )}
                    {renderDefaultDropdown(
                        'integrations.kubit.defaults.phonePrefix',
                        'Default phone prefix',
                        discovery.phonePrefixIds,
                        defaultPhonePrefixId,
                        setDefaultPhonePrefixId
                    )}

                    <div className="kubit-actions kubit-actions-spread">
                        <button
                            type="button"
                            className="kubit-btn is-primary"
                            disabled={savingDefaults || props?.buttonLoader}
                            onClick={handleSaveDefaults}
                        >
                            <FormattedMessage id="integrations.kubit.defaults.save" defaultMessage="Save defaults" />
                        </button>
                    </div>
                </div>
            )}

            {isConfigured && (
                <div className="kubit-danger-zone">
                    <p className="kubit-danger-zone-title">
                        <FormattedMessage id="integrations.kubit.dangerZone.title" defaultMessage="Advanced actions" />
                    </p>

                    <div className="kubit-danger-row">
                        <div className="kubit-danger-info">
                            <p className="kubit-danger-label">
                                <FormattedMessage id="integrations.kubit.resetCursor" defaultMessage="Reset cursors" />
                            </p>
                            <p className="kubit-danger-desc">
                                <FormattedMessage
                                    id="integrations.kubit.resetCursor.desc"
                                    defaultMessage="The next sync will re-pull everything from KuBIT from scratch. Your data is safe."
                                />
                            </p>
                        </div>
                        <button
                            type="button"
                            className="kubit-btn is-danger"
                            disabled={props?.buttonLoader}
                            onClick={() => setShowResetCursorConfirm(true)}
                        >
                            <FormattedMessage id="integrations.kubit.resetCursor" defaultMessage="Reset cursors" />
                        </button>
                    </div>

                    <div className="kubit-danger-row">
                        <div className="kubit-danger-info">
                            <p className="kubit-danger-label">
                                <FormattedMessage id="integrations.kubit.purgeMappings" defaultMessage="Purge mappings" />
                            </p>
                            <p className="kubit-danger-desc">
                                <FormattedMessage
                                    id="integrations.kubit.purgeMappings.desc"
                                    defaultMessage="Removes all links between KuBIT and Mika records. Your data is safe — only the connections between them are cleared."
                                />
                            </p>
                        </div>
                        <button
                            type="button"
                            className="kubit-btn is-danger"
                            disabled={props?.buttonLoader}
                            onClick={() => setShowPurgeMappingsConfirm(true)}
                        >
                            <FormattedMessage id="integrations.kubit.purgeMappings" defaultMessage="Purge mappings" />
                        </button>
                    </div>

                    <div className="kubit-danger-row">
                        <div className="kubit-danger-info">
                            <p className="kubit-danger-label">
                                <FormattedMessage id="integrations.kubit.disconnect" defaultMessage="Disconnect" />
                            </p>
                            <p className="kubit-danger-desc">
                                <FormattedMessage
                                    id="integrations.kubit.disconnect.desc"
                                    defaultMessage="Stops automatic syncing. Your data and links are kept, so you can reconnect later without losing anything."
                                />
                            </p>
                        </div>
                        <button
                            type="button"
                            className="kubit-btn is-danger"
                            disabled={props?.buttonLoader}
                            onClick={() => setShowDisconnectConfirm(true)}
                        >
                            <FormattedMessage id="integrations.kubit.disconnect" defaultMessage="Disconnect" />
                        </button>
                    </div>
                </div>
            )}

            <MikaConfirmModal
                isOpen={showDisconnectConfirm}
                onClose={() => setShowDisconnectConfirm(false)}
                onConfirm={handleDisconnect}
                title={intl.formatMessage({
                    id: 'integrations.kubit.disconnect.confirm.title',
                    defaultMessage: 'Disconnect KuBIT?'
                })}
                message={intl.formatMessage({
                    id: 'integrations.kubit.disconnect.confirm.message',
                    defaultMessage: 'Automatic sync will stop. Your data and ID mappings are kept, so you can reconnect later without losing them.'
                })}
                confirmLabel={intl.formatMessage({
                    id: 'integrations.kubit.disconnect',
                    defaultMessage: 'Disconnect'
                })}
                confirmColor="danger"
            />

            <MikaConfirmModal
                isOpen={showResetCursorConfirm}
                onClose={() => setShowResetCursorConfirm(false)}
                onConfirm={handleResetCursor}
                title={intl.formatMessage({
                    id: 'integrations.kubit.resetCursor.confirm.title',
                    defaultMessage: 'Reset all cursors?'
                })}
                message={intl.formatMessage({
                    id: 'integrations.kubit.resetCursor.confirm.message',
                    defaultMessage: 'Next sync will re-pull every branch, entity, item, and item price from KuBIT. Mirror tables are not deleted — they will be overwritten on the re-pull. This may take a while for tenants with thousands of items.'
                })}
                confirmLabel={intl.formatMessage({
                    id: 'integrations.kubit.resetCursor',
                    defaultMessage: 'Reset cursors'
                })}
                confirmColor="danger"
            />

            <MikaConfirmModal
                isOpen={showPurgeMappingsConfirm}
                onClose={() => setShowPurgeMappingsConfirm(false)}
                onConfirm={handlePurgeMappings}
                title={intl.formatMessage({
                    id: 'integrations.kubit.purgeMappings.confirm.title',
                    defaultMessage: 'Purge all mappings?'
                })}
                message={intl.formatMessage({
                    id: 'integrations.kubit.purgeMappings.confirm.message',
                    defaultMessage: 'Hard-deletes every KuBIT↔Mika ID mapping for this company. Used after wrong links were made. Mirror tables (kubit_branches/entities/items/item_prices) and KuBIT-side data are not affected. Next sync will re-link from scratch.'
                })}
                confirmLabel={intl.formatMessage({
                    id: 'integrations.kubit.purgeMappings',
                    defaultMessage: 'Purge mappings'
                })}
                confirmColor="danger"
            />
        </div>
    );
};

function mapStateToProps(state: RootState, ownProps: any) {
    return {
        ...ownProps,
        ...state.app,
        admin: state.admin
    };
}

function mapDispatchToProps(dispatch: Dispatch<actions.ACTION>) {
    return {
        actions: bindActionCreators(actions as any, dispatch),
        adminActions: bindActionCreators(adminActions as any, dispatch)
    };
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectIntl(KubitIntegration as any)));
